import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePostHog } from "@posthog/react";
import houseIllustration from "../../assets/dashboard/house_on_hill_corrected.svg";
import workerIllustration from "../../assets/dashboard/upper_body_worker_briefcase_thinner.svg";
import logoImage from "../../assets/logo.svg";
import LandingCategoryShowcase from "../../components/landing/LandingCategoryShowcase";
import LandingGlyph from "../../components/landing/LandingGlyph";
import LandingStatBand from "../../components/landing/LandingStatBand";
import LandingTestimonials from "../../components/landing/LandingTestimonials";
import AbstractCards from "../../components/ui/AbstractCards/AbstractCards";
import Button from "../../components/ui/Button/Button";
import FlowChart from "../../components/ui/FlowChart/FlowChart";
import Header from "../../components/ui/Header/Header";
import InstallAppModal from "../../components/ui/InstallAppModal/InstallAppModal";
import TermsContent from "../../components/legal/TermsContent";
import Modal from "../../components/ui/Modal/Modal";
import FeedbackModal from "../../components/support/FeedbackModal";
import { COMPANY_EMAIL, COMPANY_EMAIL_MAILTO } from "../../constants/company";
import { companyName } from "../../constants/constants";
import { useInstallPrompt } from "../../hooks/useInstallPrompt";
import { gigsApi } from "../../lib/gigs";
import type { LandingGigResponse } from "../../types/gigTypes";
import { formatTeenGigCompensation } from "../../utils/GigCompensationUtils";
import {
  audienceCards,
  type HeroGigCard,
  heroGigCards,
  heroTickerItems,
  heroTrustSignals,
  howItWorks,
  landingCategories,
  landingStats,
  landingTestimonials,
  landingReviewsSummary,
  navItems,
  roleButtons,
} from "./constants";

const LANDING_GIG_MINIMUM_PAYMENT_AMOUNT_CENTS = 5000;
const LANDING_GIG_MODAL_LIMIT = 5;

/**
 * The hero ticker renders this many copies of the category list so the marquee
 * animation (which shifts by exactly one copy) always has enough content queued
 * to fill the widest viewport. Keep in sync with the shift percentage in the
 * landing-marquee keyframes.
 */
const LANDING_TICKER_COPY_COUNT = 3;

const tickerCopies = Array.from({ length: LANDING_TICKER_COPY_COUNT }, (_, index) => index);

/** Positional emphasis for the hero role buttons; extra buttons fall back to outline. */
const ROLE_BUTTON_VARIANTS = ["primary", "secondary", "outline"] as const;
const reversedHeroTickerItems = [...heroTickerItems].reverse();

const toDisplayHeroGigCard = (card: HeroGigCard, liveGigs: LandingGigResponse[]): HeroGigCard => {
  const liveGig = typeof card.liveDataSlot === "number" ? liveGigs[card.liveDataSlot] : undefined;

  if (!liveGig || liveGig.paymentAmountCents <= LANDING_GIG_MINIMUM_PAYMENT_AMOUNT_CENTS) {
    return card;
  }

  return {
    ...card,
    title: liveGig.title.trim() || card.title,
    meta: liveGig.category.trim() || "Featured local gig",
    pay: formatTeenGigCompensation(liveGig.paymentAmountCents, false, card.pay),
  };
};

const getTopPricedLandingGigs = (gigs: LandingGigResponse[]) =>
  [...gigs]
    .sort((firstGig, secondGig) => secondGig.paymentAmountCents - firstGig.paymentAmountCents)
    .slice(0, LANDING_GIG_MODAL_LIMIT);

const LandingPage = () => {
  const navigate = useNavigate();
  const posthog = usePostHog();
  const [isTeenGigsOpen, setIsTeenGigsOpen] = useState(false);
  const [isGuardianControlsOpen, setIsGuardianControlsOpen] = useState(false);
  const [isPostGigInfoOpen, setIsPostGigInfoOpen] = useState(false);
  const [isDisclaimerOpen, setIsDisclaimerOpen] = useState(false);
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [featuredLandingGigs, setFeaturedLandingGigs] = useState<LandingGigResponse[]>([]);
  const { isInstalled, isInstallHelpOpen, setIsInstallHelpOpen, handleInstallClick } = useInstallPrompt();
  const topPricedLandingGigs = useMemo(() => getTopPricedLandingGigs(featuredLandingGigs), [featuredLandingGigs]);

  const disclaimerSections = [
    "MyTeenGig does not guarantee or warrant the quality, safety, legality, or suitability of any work performed by a teen. Posters are solely responsible for evaluating and verifying a teen's qualifications, ability, and fitness to perform any requested gig.",
    "MyTeenGig is not liable for any damage to property, personal injury, or any other loss or harm arising out of or related to any gig, interaction, or use of the website.",
    "MyTeenGig is not responsible for gigs that are not completed, are completed late, or are completed in a manner that is unsatisfactory to the poster. Posters are responsible for appropriately managing, supervising, and overseeing gigs as needed. Use of this website and reliance on its services is at your own risk.",
    "All information, services, and content on this website are provided on an \"AS IS\" and \"AS AVAILABLE\" basis. MyTeenGig makes no representations or warranties, express or implied, regarding the accuracy, reliability, completeness, availability, or suitability of the website, its content, or its services.",
    "To the maximum extent permitted by law, MyTeenGig shall not be liable for any direct, indirect, incidental, consequential, special, or punitive damages arising out of or related to the use of, or inability to use, this website or its services.",
    "This website may allow users to post comments, reviews, messages, or other content. MyTeenGig does not endorse and is not responsible for any opinions, advice, statements, or other content posted by third-party users. Any content submitted by users remains the responsibility of the person who submitted it, not MyTeenGig.",
    "By using this website, you acknowledge that you may be exposed to content that is inaccurate, misleading, offensive, or otherwise objectionable, and you agree that MyTeenGig is not liable for any user-generated content.",
    "Users are solely responsible for their interactions with other users and third parties through the website. MyTeenGig is not liable for any disputes, claims, losses, injuries, damages, or harms of any kind arising out of or related to user conduct, gigs, or interactions with other parties. By using this website, you agree to release MyTeenGig from any claims, demands, and damages arising out of or connected with disputes or interactions with third parties or other users.",
    "Third-Party Links: This website may contain links to third-party websites or services that are not owned or controlled by MyTeenGig. MyTeenGig has no control over, and assumes no responsibility for, the content, privacy policies, practices, products, or services of any third-party websites or services. You acknowledge and agree that MyTeenGig shall not be responsible or liable, directly or indirectly, for any damage or loss caused by or in connection with the use of, or reliance on, any such content, goods, or services available through any third-party websites or services.",
    "By using this website, you agree to comply with MyTeenGig's rules, policies, and terms. This agreement shall be governed by the laws of the State of New York, and the parties submit to the exclusive jurisdiction of the state and federal courts located in New York.",
  ];

  const navigateToSignup = (signupRole: "TEEN" | "ADULT", signupAccountType?: "BUSINESS") => {
    const accountTypeQuery = signupAccountType ? `&accountType=${signupAccountType}` : "";
    navigate(`/signup?role=${signupRole}${accountTypeQuery}`);
  };

  const handleAudienceCtaClick = (card: (typeof audienceCards)[number]) => {
    if (card.ctaLabel === "Explore Teen Gigs") {
      setIsTeenGigsOpen(true);
      return;
    }

    if (card.ctaLabel === "Learn About Parent/Guardian Controls") {
      setIsGuardianControlsOpen(true);
      return;
    }

    if (card.ctaLabel === "Post a Gig") {
      setIsPostGigInfoOpen(true);
    }
  };

  const handlePostGigSignupClick = () => {
    setIsPostGigInfoOpen(false);
    posthog?.capture("adult_post_job_intent_started", { source: "signup" });
    navigate("/signup?role=ADULT&intent=post-job");
  };

  const handlePostGigLoginClick = () => {
    setIsPostGigInfoOpen(false);
    posthog?.capture("adult_post_job_intent_started", { source: "login" });
    navigate("/login?redirect=%2Fdashboard%3Fintent%3Dpost-job");
  };

  useEffect(() => {
    let isCurrent = true;

    void gigsApi.listLandingFeaturedGigs()
      .then((gigs) => {
        if (isCurrent) {
          const publicGigs = Array.isArray(gigs) ? gigs : [];
          setFeaturedLandingGigs(publicGigs);
        }
      })
      .catch(() => {
        if (isCurrent) {
          setFeaturedLandingGigs([]);
        }
      });

    return () => {
      isCurrent = false;
    };
  }, []);

  return (
    <div className="min-h-screen overflow-x-hidden bg-white text-myteengig-text-primary">
      <Header
        sticky
        bordered={false}
        navItems={navItems}
        brand={
          <span className="inline-flex items-center gap-3 tracking-normal">
            <img src={logoImage} alt={`${companyName} logo`} className="h-11 w-11 shrink-0 sm:h-12 sm:w-12" />
            <span className="hidden text-2xl font-bold text-myteengig-text-primary md:inline">{companyName}</span>
          </span>
        }
        rightSlot={
          <>
            <Button variant="primary" size="sm" className="h-9 min-w-[78px] justify-center px-3 text-xs whitespace-nowrap" onClick={() => navigate("/signup")}>
              Sign up
            </Button>
            <Button variant="outline" size="sm" className="h-9 min-w-[78px] justify-center px-3 text-xs whitespace-nowrap" onClick={() => navigate("/login")}>
              Log in
            </Button>
          </>
        }
        mobileMenuEnabled={false}
        containerClassName="!max-w-none px-4 py-3 sm:px-6 lg:px-10"
        navClassName="text-myteengig-text-primary/75"
        className="bg-white/85 shadow-[0_1px_24px_rgba(10,70,102,0.09)] backdrop-blur-xl supports-[backdrop-filter]:bg-white/75"
      />

      <main>
        <section className="relative isolate flex min-h-[calc(100svh-4.75rem)] overflow-hidden px-4 py-5 sm:px-6 sm:py-7 lg:min-h-[calc(100svh-5.5rem)] lg:px-10">
          <div className="landing-hero-surface absolute inset-0" />
          <div className="landing-grid-motion absolute inset-0 opacity-70" />
          <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(255,255,255,0.96)_0%,rgba(255,255,255,0.82)_48%,rgba(255,255,255,0.42)_100%)]" />

          <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
            <div className="landing-marquee-left absolute left-0 top-5 flex w-max opacity-75">
              {tickerCopies.map((copyIndex) => (
                <div key={`top-copy-${copyIndex}`} className="flex shrink-0 gap-2 pr-2">
                  {heroTickerItems.map((item) => (
                    <span
                      key={`top-${copyIndex}-${item}`}
                      className="rounded-md border border-white/80 bg-white/80 px-3 py-1.5 text-xs font-semibold text-myteengig-text-primary shadow-sm backdrop-blur"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              ))}
            </div>
            <div className="landing-marquee-right absolute bottom-5 left-0 hidden w-max opacity-70 sm:flex">
              {tickerCopies.map((copyIndex) => (
                <div key={`bottom-copy-${copyIndex}`} className="flex shrink-0 gap-2 pr-2">
                  {reversedHeroTickerItems.map((item) => (
                    <span
                      key={`bottom-${copyIndex}-${item}`}
                      className="rounded-md border border-white/75 bg-white/70 px-3 py-1.5 text-xs font-semibold text-myteengig-text-muted shadow-sm backdrop-blur"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              ))}
            </div>

            <img
              src={houseIllustration}
              alt=""
              className="landing-float-slow absolute bottom-[-1.5rem] left-[-4rem] w-[18rem] max-w-[54vw] opacity-25 sm:w-[24rem] sm:opacity-55 lg:w-[34rem]"
            />
            <img
              src={workerIllustration}
              alt=""
              className="landing-float absolute right-[-3rem] bottom-[-2rem] w-[13rem] opacity-30 sm:w-[18rem] sm:opacity-70 md:right-[-1rem] lg:w-[25rem]"
            />

            {heroGigCards.map((card) => (
              (() => {
                const displayCard = toDisplayHeroGigCard(card, featuredLandingGigs);

                return (
                  <article
                    key={card.title}
                    className={`absolute rounded-lg border px-4 py-3 shadow-[0_18px_60px_rgba(10,70,102,0.13)] backdrop-blur-xl ${displayCard.toneClassName} ${displayCard.positionClassName}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-xs font-semibold">Open gig</p>
                        <h3 className="mt-1 text-base font-bold">{displayCard.title}</h3>
                        <p className="mt-1 text-xs opacity-80">{displayCard.meta}</p>
                      </div>
                      <p className="rounded-md bg-white/70 px-2 py-1 text-sm font-black">{displayCard.pay}</p>
                    </div>
                  </article>
                );
              })()
            ))}

            <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-white/95 via-white/50 to-transparent" />
          </div>

          <div className="relative z-10 mx-auto flex w-full max-w-7xl flex-col justify-center py-3 text-center sm:py-7">
            <div className="mx-auto max-w-5xl">
              <h1 className="mt-8 text-5xl leading-none font-black text-myteengig-text-primary sm:mt-0 sm:text-6xl md:text-7xl lg:text-8xl">
                {companyName}
              </h1>
              <p className="mx-auto mt-4 max-w-3xl text-lg leading-7 font-semibold text-[#0F3F4E] sm:text-xl md:text-2xl">
                Local gigs for teens, posted by families and businesses, with parent/guardian oversight built in.
              </p>
              <div className="mx-auto mt-5 grid max-w-5xl grid-cols-2 gap-2 lg:grid-cols-4">
                {roleButtons.map(({ label, subheading, signupRole, signupAccountType, iconSrc, iconAlt }, index) => (
                  <Button
                    key={label}
                    variant={ROLE_BUTTON_VARIANTS[index] ?? "outline"}
                    className={`landing-action-sheen flex min-h-[6.5rem] w-full flex-col items-center justify-start gap-2 px-2 py-2 text-center shadow-lg transition-transform hover:-translate-y-0.5 sm:min-h-16 sm:flex-row sm:justify-start sm:gap-3 sm:px-3 sm:py-3 sm:text-left ${
                      index === 0 ? "shadow-myteengig-primary/20" : "bg-white/90 backdrop-blur"
                    }`}
                    onClick={() => navigateToSignup(signupRole, signupAccountType)}
                  >
                    <span className={`relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-md sm:h-10 sm:w-10 ${index === 0 ? "bg-white/20" : "bg-myteengig-primary"}`}>
                      <img src={iconSrc} alt={iconAlt} className="h-5 w-5 object-contain" />
                    </span>
                    <span className="relative z-10 min-w-0">
                      <span className="block text-xs leading-4 font-bold sm:text-base sm:leading-5">{label}</span>
                      <span className={`block text-[11px] leading-4 sm:text-xs sm:leading-5 ${index === 0 ? "text-white/85" : "text-myteengig-text-muted"}`}>
                        {subheading}
                      </span>
                    </span>
                  </Button>
                ))}
              </div>

              <div className="mt-4 flex flex-col items-center justify-center gap-2 sm:flex-row">
                <Button
                  variant="secondary"
                  size="md"
                  className="min-h-10 min-w-[220px] justify-center"
                  onClick={() => void handleInstallClick()}
                  disabled={isInstalled}
                >
                  {isInstalled ? "Added to Home Screen" : "Add to Home Screen"}
                </Button>
                <a
                  href="#how-it-works"
                  className="inline-flex min-h-10 items-center justify-center rounded-md border border-myteengig-primary/20 bg-white/80 px-4 text-sm font-semibold text-myteengig-text-primary shadow-sm backdrop-blur transition hover:border-myteengig-primary hover:bg-white"
                >
                  How it works
                </a>
              </div>
            </div>

            <div className="mx-auto mt-5 hidden max-w-6xl grid-cols-3 gap-3 md:grid">
              {heroTrustSignals.map((signal) => (
                <article key={signal.title} className="flex items-start gap-3 rounded-lg border border-white/75 bg-white/78 px-4 py-3 text-left shadow-sm backdrop-blur">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-myteengig-primary/10 text-myteengig-primary">
                    <LandingGlyph name={signal.glyph} className="h-5 w-5" />
                  </span>
                  <div className="min-w-0">
                    <h2 className="text-sm font-bold text-myteengig-text-primary">{signal.title}</h2>
                    <p className="mt-1 text-sm leading-6 text-myteengig-text-muted">{signal.body}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <LandingStatBand stats={landingStats} />

        <section id="how-it-works" className="bg-white px-4 py-16 sm:px-6 lg:px-10">
          <div className="mx-auto max-w-7xl">
            <p className="inline-flex items-center gap-2 rounded-md border border-myteengig-border bg-myteengig-background px-3 py-1.5 text-xs font-bold tracking-wide text-myteengig-primary uppercase shadow-sm">
              <span className="h-1.5 w-1.5 rounded-full bg-myteengig-primary" />
              The flow
            </p>
            <h2 className="mt-3 text-3xl font-bold text-myteengig-text-primary">How It Works</h2>
            <p className="mt-3 max-w-3xl text-base leading-7 text-myteengig-text-muted">
              The flow is built around clear expectations before the gig and visibility while the work moves forward.
            </p>
            <FlowChart steps={howItWorks} className="mt-8" />
          </div>
        </section>

        <LandingCategoryShowcase
          id="gig-types"
          categories={landingCategories}
          eyebrow="Gig types"
          title="The kind of help teens take on"
          description="Gigs stay in public, low-risk categories that fit around school, sports, and seasonal schedules. Posters describe the work, the timing, and the pay before anyone applies."
          footnote="Businesses can also post ongoing listings for weekend and after-school coverage."
        />

        <section className="bg-[#0A4666] px-4 py-16 text-white sm:px-6 lg:px-10">
          <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[0.85fr_1.15fr] lg:items-center">
            <div>
              <h2 className="text-3xl font-bold">{`Why ${companyName}?`}</h2>
              <p className="mt-4 text-lg leading-8 text-white/82">
                Teens want experience. Parents/guardians want oversight. Communities need help.
              </p>
              <p className="mt-3 leading-7 text-white/72">
                The platform brings structure to the informal teen gigs that already exist with added transparency and accountability.
              </p>
            </div>
            <div className="grid gap-3 md:grid-cols-3">
              {heroTrustSignals.map((signal) => (
                <article
                  key={`why-${signal.title}`}
                  className="rounded-lg border border-white/15 bg-white/10 p-4 shadow-sm transition duration-200 hover:-translate-y-1 hover:border-white/30 hover:bg-white/15"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-white/15 text-white">
                    <LandingGlyph name={signal.glyph} className="h-6 w-6" />
                  </span>
                  <h3 className="mt-3 text-base font-bold">{signal.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-white/74">{signal.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <LandingTestimonials
          id="reviews"
          testimonials={landingTestimonials}
          averageRating={landingReviewsSummary.averageRating}
          averageLabel={`Average of all ${landingReviewsSummary.totalReviews} reviews`}
          eyebrow="Reviews"
          title="What teens and posters say"
          description="Every completed gig can be reviewed by both sides, so reputations are built on finished work instead of promises."
        />

        <AbstractCards cards={audienceCards} className="max-w-7xl px-4 sm:px-6 lg:px-10" onCtaClick={handleAudienceCtaClick} />

        <section id="about" className="bg-white px-4 py-16 sm:px-6 lg:px-10">
          <div className="mx-auto max-w-4xl text-center">
            <h2 className="text-3xl font-bold text-myteengig-text-primary">{`About ${companyName}`}</h2>
            <p className="mx-auto mt-4 max-w-3xl text-myteengig-text-muted">
              As parents/guardians of teens, we watched our kids want work for independence, responsibility, and pride. Traditional gigs often
              required rigid commitments that did not fit school, sports, and seasonal schedules.
            </p>
            <p className="mx-auto mt-4 max-w-3xl text-myteengig-text-muted">
              We saw a gap between teens who wanted flexible work and homeowners or small businesses that needed short-term help. So we built the
              platform to create flexible, local opportunities with clear expectations, parent/guardian awareness, and structured safety
              guidelines.
            </p>
            <p className="mx-auto mt-4 max-w-3xl text-myteengig-text-muted">
              Our hope is simple: help teens build confidence and responsibility while helping families and communities stay connected.
            </p>
          </div>
        </section>
      </main>

      <footer className="bg-myteengig-text-primary px-4 py-8 text-sm text-white/90 sm:px-6 lg:px-10">
        <div className="mx-auto max-w-7xl">
          <div className="mx-auto max-w-3xl text-center">
            <p>
              MyTeenGig earns revenue from gig posting fees. Teens receive their full agreed-upon pay from the gig poster via Stripe&apos;s secure
              payment platform.
            </p>
            <p className="mt-2">MyTeenGig does not employ teens and does not process wages or payroll.</p>
            <p className="mt-2">
              <a
                href="https://stripe.com/payments"
                target="_blank"
                rel="noreferrer"
                className="whitespace-nowrap text-white underline underline-offset-2 transition hover:text-white/75"
              >
                learn more
              </a>
            </p>
          </div>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-white">
            <a
              href="https://www.instagram.com/myteengig.co?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw=="
              target="_blank"
              rel="noreferrer"
              className="transition hover:text-white/75"
            >
              Instagram
            </a>
            <a
              href="https://www.facebook.com/share/18KYgRpsAr/?mibextid=wwXIfr"
              target="_blank"
              rel="noreferrer"
              className="transition hover:text-white/75"
            >
              Facebook
            </a>
            <a
              href="https://www.tiktok.com/@myteengig?_r=1&_t=ZP-94aOA9VtWbH"
              target="_blank"
              rel="noreferrer"
              className="transition hover:text-white/75"
            >
              TikTok
            </a>
          </div>
          <div className="mt-6 flex flex-col items-center gap-2 text-center text-white/80">
            <p>{`© 2026 ${companyName}`}</p>
            <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
              <button type="button" onClick={() => setIsDisclaimerOpen(true)} className="transition hover:text-white">
                Disclaimer
              </button>
              <span aria-hidden="true">•</span>
              <button type="button" onClick={() => setIsFeedbackOpen(true)} className="transition hover:text-white">
                Feedback
              </button>
              <span aria-hidden="true">•</span>
              <button type="button" onClick={() => setIsTermsOpen(true)} className="transition hover:text-white">
                Terms
              </button>
              <span aria-hidden="true">•</span>
              <a href="#" className="transition hover:text-white">
                Privacy
              </a>
              <span aria-hidden="true">•</span>
              <a href={COMPANY_EMAIL_MAILTO} className="transition hover:text-white">
                {COMPANY_EMAIL}
              </a>
            </div>
          </div>
        </div>
      </footer>

      <InstallAppModal isOpen={isInstallHelpOpen} onClose={() => setIsInstallHelpOpen(false)} />

      <Modal isOpen={isTeenGigsOpen} onClose={() => setIsTeenGigsOpen(false)} title="See Current Gigs On MyTeenGig" panelClassName="max-w-3xl">
        <div className="space-y-4 text-left">
          <p className="text-sm leading-6 text-myteengig-text-muted">
            A limited public preview of five gigs currently available.
          </p>
          {topPricedLandingGigs.length > 0 ? (
            <ol className="space-y-3">
              {topPricedLandingGigs.map((gig, index) => {
                const title = gig.title.trim() || "Untitled gig";
                const category = gig.category.trim() || "Local gig";

                return (
                  <li
                    key={`${title}-${category}-${gig.paymentAmountCents}-${index}`}
                    className="rounded-lg border border-myteengig-border bg-myteengig-background/60 p-4"
                  >
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <p className="text-xs font-bold tracking-wide text-myteengig-primary uppercase">{`#${index + 1} • ${category}`}</p>
                        <h3 className="mt-1 break-words text-lg font-semibold text-myteengig-text-primary">{title}</h3>
                      </div>
                      <p className="shrink-0 rounded-md bg-white px-3 py-2 text-base font-black text-myteengig-text-primary shadow-sm">
                        {formatTeenGigCompensation(gig.paymentAmountCents, false, "Paid")}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ol>
          ) : (
            <div className="rounded-lg border border-dashed border-myteengig-border bg-myteengig-background/60 p-5">
              <p className="font-semibold text-myteengig-text-primary">No paid gigs are available to preview right now.</p>
              <p className="mt-2 text-sm leading-6 text-myteengig-text-muted">
                New gigs appear as families and businesses post available work.
              </p>
            </div>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={isGuardianControlsOpen}
        onClose={() => setIsGuardianControlsOpen(false)}
        title="Parent/Guardian Controls"
        panelClassName="max-w-3xl"
      >
        <div className="space-y-5 text-left text-sm leading-6 text-myteengig-text-muted">
          <p>
            Parents and guardians can connect to a teen account to keep visibility into gig activity and respond when a teen needs approval.
          </p>
          <div className="grid gap-3 md:grid-cols-2">
            <article className="rounded-lg border border-myteengig-border bg-myteengig-background/60 p-4">
              <h3 className="font-semibold text-myteengig-text-primary">Connected Accounts</h3>
              <p className="mt-2">
                Teens and adults can send guardian connection requests. Once accepted, the guardian can access the teen's guardian profile.
              </p>
            </article>
            <article className="rounded-lg border border-myteengig-border bg-myteengig-background/60 p-4">
              <h3 className="font-semibold text-myteengig-text-primary">Gig Insight</h3>
              <p className="mt-2">
                Guardians can see applied gigs, proposed timing, status, category, location summary, teen messages, and pay details.
              </p>
            </article>
            <article className="rounded-lg border border-myteengig-border bg-myteengig-background/60 p-4">
              <h3 className="font-semibold text-myteengig-text-primary">Approval Flow</h3>
              <p className="mt-2">
                Under-18 teens are prompted to connect an active guardian before applying. When approval is required, guardians can approve or decline the request.
              </p>
            </article>
            <article className="rounded-lg border border-myteengig-border bg-myteengig-background/60 p-4">
              <h3 className="font-semibold text-myteengig-text-primary">Activity Summary</h3>
              <p className="mt-2">
                Guardian views include completed gigs, scheduled gigs, request-stage gigs, reviews, business applications, earnings, and payout setup status.
              </p>
            </article>
          </div>
          <p>
            For paid gigs, the selected payout guardian can complete Stripe payout onboarding when the teen uses guardian-managed payouts.
          </p>
        </div>
      </Modal>

      <Modal
        isOpen={isPostGigInfoOpen}
        onClose={() => setIsPostGigInfoOpen(false)}
        title="Posting a Gig Is Simple"
        panelClassName="max-w-3xl"
      >
        <div className="space-y-5 text-left">
          <p className="text-sm leading-6 text-myteengig-text-muted">
            Sign up as a homeowner or business, then MyTeenGig walks you through a short gig post. Add what you need, when you need it, where the work is, and what it pays.
          </p>
          <div className="grid gap-3 md:grid-cols-3">
            <article className="rounded-lg border border-myteengig-border bg-myteengig-background/60 p-4">
              <h3 className="font-semibold text-myteengig-text-primary">Create an Account</h3>
              <p className="mt-2 text-sm leading-6 text-myteengig-text-muted">
                Start with a quick adult signup so your gigs and applicants stay organized in one place.
              </p>
            </article>
            <article className="rounded-lg border border-myteengig-border bg-myteengig-background/60 p-4">
              <h3 className="font-semibold text-myteengig-text-primary">Describe the Work</h3>
              <p className="mt-2 text-sm leading-6 text-myteengig-text-muted">
                Pick a category, add the schedule, set pay, and write clear expectations for local teens.
              </p>
            </article>
            <article className="rounded-lg border border-myteengig-border bg-myteengig-background/60 p-4">
              <h3 className="font-semibold text-myteengig-text-primary">Post When Ready</h3>
              <p className="mt-2 text-sm leading-6 text-myteengig-text-muted">
                Review the details, publish the gig, and manage teen interest from your account.
              </p>
            </article>
          </div>
          <p className="rounded-lg border border-myteengig-primary/20 bg-myteengig-primary/5 p-4 text-sm leading-6 text-myteengig-text-muted">
            It is built for short, clearly defined work like yard help, pet care, event setup, errands, tutoring, tech help, and business shifts.
          </p>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button variant="primary" className="justify-center" onClick={handlePostGigSignupClick}>
              Sign up to post a gig
            </Button>
            <Button variant="outline" className="justify-center" onClick={handlePostGigLoginClick}>
              Log in
            </Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={isDisclaimerOpen} onClose={() => setIsDisclaimerOpen(false)} title={`${companyName} Disclaimer`} panelClassName="max-w-3xl">
        <div className="max-h-[70vh] space-y-4 overflow-y-auto pr-1 text-left text-sm leading-relaxed text-myteengig-text-muted">
          {disclaimerSections.map((section) => (
            <p key={section}>{section}</p>
          ))}
        </div>
      </Modal>

      <Modal isOpen={isTermsOpen} onClose={() => setIsTermsOpen(false)} title="Terms of Service" panelClassName="max-w-4xl" bodyClassName="pr-0">
        <TermsContent className="pr-2" />
      </Modal>

      <FeedbackModal isOpen={isFeedbackOpen} onClose={() => setIsFeedbackOpen(false)} />
    </div>
  );
};

export default LandingPage;
